import React, { useEffect, useState } from "react";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./Theme.css";
import "./DlciQuestionaire.css";

const PARAMETERS = ["Very much", "A lot", "Little", "Not at all", "Not applicable"];

const calculateScore = (parameter) => {
  switch (parameter) {
    case "Very much": return 3;
    case "A lot": return 2;
    case "Little": return 1;
    default: return 0;
  }
};

const calculateId = (parameter) => {
  switch (parameter) {
    case "Very much": return 5;
    case "A lot": return 4;
    case "Little": return 3;
    case "Not at all": return 2;
    case "Not applicable": return 1;
    default: return 0;
  }
};

const getConditionTitle = () => {
  const host = window.location.hostname;
  if (host === "lifestyle1.goqii.com") return "PSORIASIS";
  return "ATOPIC DERMATITIS";
};

const DlciQuestionaireOne = () => {
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";
  const pfizerOnboardingComplete = urlParams.get("pfizerOnboardingComplete") || "";

  const title = getConditionTitle();
  const reportUrl = "https://www.health.state.mn.us/communities/ep/surge/burn/tbsa.html";

  const [questions, setQuestions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [logQuestion, setLogQuestion] = useState({});
  const [rating, setRating] = useState(0);
  const [page, setPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [showDonePopup, setShowDonePopup] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  useEffect(() => {
    lifestyleApi
      .fetchDlqiQuestions()
      .then((res) => {
        setQuestions(res?.data?.data?.logResponse || []);
      })
      .catch((err) => console.error("fetchDlqiQuestions:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let total = 0;
    for (const q in selectedOptions) {
      total += calculateScore(selectedOptions[q]);
    }
    setRating(total);
  }, [selectedOptions]);

  const handleSelect = (questionId, questionText, parameter) => {
    setSelectedOptions((prev) => ({ ...prev, [questionText]: parameter }));
    setLogQuestion((prev) => ({ ...prev, [questionId]: calculateId(parameter) }));
  };

  const buildQs = () =>
    `uuid=${uuid}&goqiiuserid=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&theme=${theme}&lang=${lang}`;

  const pageQuestions = page === 1 ? questions.slice(0, 5) : questions.slice(5, 10);
  const page1Done = pageQuestions.every((q) => selectedOptions[q.questions] !== undefined) && page === 1
    ? questions.slice(0, 5).every((q) => selectedOptions[q.questions] !== undefined)
    : true;
  const firstHalfDone = questions.slice(0, 5).every((q) => selectedOptions[q.questions] !== undefined);
  const allDone = questions.every((q) => selectedOptions[q.questions] !== undefined);

  const handleNext = () => {
    if (page === 1 && firstHalfDone) {
      setPage(2);
    }
  };

  const handleDone = () => {
    if (!allDone || submitting) return;
    if (pfizerOnboardingComplete === "Y") {
      setShowDonePopup(true);
      return;
    }
    submitDlqi();
  };

  const submitDlqi = () => {
    setSubmitting(true);
    lifestyleApi
      .logDlci({ goqiiUserId, logQuestions: JSON.stringify(logQuestion), score: rating })
      .then((res) => {
        if (String(res?.data?.code) === "200") {
          if (pfizerOnboardingComplete === "Y") {
            window.location.href = "https://goqii.com/app?pfizerOnboardingComplete=Done";
          } else {
            window.location.href = `/dlqidetails?${buildQs()}&type=dlci`;
          }
        }
      })
      .catch((err) => console.error("logDlci:", err))
      .finally(() => setSubmitting(false));
  };

  if (loading) {
    return (
      <section className="dlqione-page">
        <div className="dlqione-loading">Loading...</div>
      </section>
    );
  }

  return (
    <>
      <section className="dlqione-page">
        <div className="dlqione-container">
          {title === "ATOPIC DERMATITIS" && (
            <p className="dlqione-intro">
              Dermatology Life Quality Index is a simple questionnaire designed to see the impact of AD in your lives
            </p>
          )}

          <div className="dlqione-table-card">
            <label className="dlqione-label">
              How the {title} affected your life in the past one week?
            </label>

            <div className="dlqione-table-wrap">
              <table className="dlci-table">
                <thead>
                  <tr>
                    <th className="dlci-th dlci-q-col"></th>
                    {PARAMETERS.map((p) => (
                      <th key={p} className="dlci-th dlci-opt-col">{p}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageQuestions.map((qObj) => (
                    <tr key={qObj.questionId}>
                      <td className="dlci-td dlci-q-text">{qObj.questions}</td>
                      {PARAMETERS.map((p) => {
                        const checked = selectedOptions[qObj.questions] === p;
                        return (
                          <td key={p} className="dlci-td dlci-radio-cell">
                            <label className="dlci-radio-wrap">
                              <input
                                type="radio"
                                name={`q_${qObj.questionId}`}
                                value={p}
                                checked={checked}
                                onChange={() => handleSelect(qObj.questionId, qObj.questions, p)}
                              />
                              <span className={`dlci-radio${checked ? " checked" : ""}`} />
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="dlqione-page-indicator">Page {page} of 2</div>
          </div>
        </div>
      </section>

      <section className="dlqione-bottom-btn">
        <p className="dlqione-note">
          Adapted from DLQI:{" "}
          <a
            href={`https://goqii.com/app?fai=${encodeURIComponent(JSON.stringify({ url: reportUrl, title: "DLQI" }))}`}
          >
            Know More
          </a>
        </p>

        {page === 1 ? (
          <button
            type="button"
            className="dlqione-btn-done"
            onClick={handleNext}
            disabled={!firstHalfDone}
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            className="dlqione-btn-done"
            onClick={handleDone}
            disabled={!allDone || submitting}
          >
            Done
          </button>
        )}
      </section>

      {showDonePopup && (
        <div className="dlqione-popup-overlay">
          <div className="dlqione-popup-box">
            <p className="dlqione-popup-title">Don't Worry!</p>
            <p className="dlqione-popup-text">
              You will be guided by our care team on BSA and DLQI in upcoming Skin Coach interaction.
            </p>
            <button
              type="button"
              className="dlqione-btn-done"
              onClick={() => { setShowDonePopup(false); submitDlqi(); }}
            >
              Ok
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DlciQuestionaireOne;
